<template>
  <div class="arp-integrada">
    <div class="arp-integrada__colunas">
      <div class="arp-integrada__coluna">
        <section class="arp-integrada__card arp-integrada__documentos">
          <div class="arp-integrada__section-head">
            <h4 class="arp-integrada__section-title">Documentos</h4>
            <b-tooltip :label="pncpDocumentoUploadTooltip" position="is-left" multilined>
              <b-upload
                v-model="arquivoDocumento"
                :disabled="documentosFormDesabilitado"
                @input="onCarregarDocumento"
              >
                <a
                  class="button is-primary is-small"
                  :class="{ 'is-disabled': documentosFormDesabilitado }"
                >
                  <b-icon icon="upload" />
                  <span>Adicionar Novo Documento</span>
                </a>
              </b-upload>
            </b-tooltip>
          </div>

          <ul v-if="documentos.length" class="arp-integrada__list">
            <li v-for="documento in documentos" :key="documento.id" class="arp-integrada__item">
              <div class="arp-integrada__item-nome">
                <a v-if="documento.arquivo" :href="documento.arquivo | fileServer" download>
                  {{ documento.nome }}
                </a>
                <span v-else>{{ documento.nome }}</span>
              </div>

              <div v-if="podeExcluirDocumento(documento)" class="arp-integrada__item-actions">
                <b-tooltip
                  v-if="documentoSincronizadoPncp(documento)"
                  label="Excluir"
                  position="is-left"
                >
                  <button
                    type="button"
                    class="button is-danger arp-integrada__btn-excluir"
                    :disabled="documentosFormDesabilitado"
                    @click="$emit('excluir-documento', { ata, documento })"
                  >
                    <b-icon icon="delete" />
                  </button>
                </b-tooltip>
                <b-tooltip v-else label="Aguardando sincronização" position="is-left">
                  <span class="arp-integrada__status-icon is-pending" aria-hidden="true">
                    <b-icon icon="clock-outline" size="is-small" />
                  </span>
                </b-tooltip>
              </div>
            </li>
          </ul>
          <div v-else class="arp-integrada__empty">
            <p class="has-text-grey has-text-centered">Nenhum documento cadastrado.</p>
          </div>
        </section>
      </div>

      <div class="arp-integrada__coluna">
        <section class="arp-integrada__card arp-integrada__partes">
          <div class="arp-integrada__section-head">
            <h4 class="arp-integrada__section-title">Partes Envolvidas</h4>
          </div>

          <b-field grouped class="arp-integrada__partes-form column-direction-touch">
            <b-field
              expanded
              class="arp-integrada__partes-orgao"
              :type="partePncpInvalida ? 'is-danger' : ''"
              :message="
                partePncpInvalida ? 'Informe o CNPJ e selecione a unidade cadastrada no PNCP.' : ''
              "
            >
              <b-autocomplete
                ref="partePncp"
                v-model="parteBusca"
                class="arp-integrada__partes-autocomplete auction-responsible-autocomplete"
                placeholder="CNPJ"
                field="label"
                open-on-focus
                keep-first
                :disabled="partesFormDesabilitado"
                :data="opcoesPartePncpFiltradas"
                :loading="carregandoUnidadesPncp"
                @input="onParteBuscaInput"
                @select="onSelecionarPartePncp"
              >
                <template v-slot:empty>
                  {{
                    opcoesPartePncp.length
                      ? 'Nenhuma unidade para essa busca'
                      : 'Informe um CNPJ para pesquisar'
                  }}
                </template>

                <template v-slot="props">
                  <div class="arp-integrada__parte-opcao">
                    <p class="arp-integrada__parte-linha-principal">
                      <strong>{{ nomeExibicaoOpcaoPncp(props.option) }}</strong>
                      <span class="arp-integrada__parte-documento">{{
                        formatarDocumentoParte(props.option.cnpj)
                      }}</span>
                    </p>
                    <p
                      v-if="detalheUnidadeOpcaoPncp(props.option)"
                      class="arp-integrada__parte-linha-secundaria"
                    >
                      {{ detalheUnidadeOpcaoPncp(props.option) }}
                    </p>
                  </div>
                </template>
              </b-autocomplete>
            </b-field>

            <b-field v-if="permiteAdesao" class="arp-integrada__partes-tipo">
              <b-select
                v-model="parteForm.tipoParticipante"
                expanded
                :disabled="partesFormDesabilitado"
              >
                <option value="PARTICIPANTE">Participante</option>
                <option value="NAO_PARTICIPANTE">Não Participante (Carona)</option>
              </b-select>
            </b-field>

            <b-field class="arp-integrada__partes-adicionar">
              <div class="field">
                <button
                  type="button"
                  class="button is-primary"
                  :disabled="partesFormDesabilitado"
                  @click="adicionarParte"
                >
                  Adicionar
                </button>
              </div>
            </b-field>
          </b-field>

          <ul v-if="partesEnvolvidasVisiveis.length" class="arp-integrada__list">
            <li
              v-for="parte in partesEnvolvidasVisiveis"
              :key="parte.id"
              class="arp-integrada__item"
            >
              <div class="arp-integrada__item-nome">
                <p class="arp-integrada__parte-linha-principal">
                  <strong>{{ nomeExibicaoParte(parte) }}</strong>
                  <span class="arp-integrada__parte-documento">{{
                    formatarDocumentoParte(parte.documento)
                  }}</span>
                </p>
                <p v-if="detalheUnidadeParte(parte)" class="arp-integrada__parte-linha-secundaria">
                  {{ detalheUnidadeParte(parte) }}
                </p>
              </div>

              <div class="arp-integrada__item-actions">
                <span class="tag arp-integrada__parte-tag">
                  {{ rotuloTipoParticipante(parte.tipoParticipante) }}
                </span>

                <template v-if="podeExcluirParte(parte)">
                  <b-tooltip v-if="parteSincronizadaPncp(parte)" label="Excluir" position="is-left">
                    <button
                      type="button"
                      class="button is-danger arp-integrada__btn-excluir"
                      :disabled="partesFormDesabilitado"
                      @click="$emit('excluir-parte', { ata, parte })"
                    >
                      <b-icon icon="delete" />
                    </button>
                  </b-tooltip>
                  <b-tooltip v-else label="Aguardando sincronização" position="is-left">
                    <span class="arp-integrada__status-icon is-pending" aria-hidden="true">
                      <b-icon icon="clock-outline" size="is-small" />
                    </span>
                  </b-tooltip>
                </template>
              </div>
            </li>
          </ul>
          <div v-else class="arp-integrada__empty">
            <p class="has-text-grey has-text-centered">Nenhuma parte envolvida cadastrada.</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import { isValid as isValidCnpj } from '@fnando/cnpj';
import { fileServer } from '@/utils/functions';
import { formatarCnpj } from '@/utils/format';
import { normalizarDocumento as limparDocumento } from '@/utils/documento';
import { consultarAtaPrecoParteEnvolvidaPncpUnidades } from '@/resources/ofertasCompra';
import {
  TIPO_DOCUMENTO_ARP_PRINCIPAL,
  TIPO_PARTE_GERENCIADORA,
  PNCP_DOCUMENT_UPLOAD_TOOLTIP,
} from '../helpers';

const TIPO_PARTICIPANTE = {
  PARTICIPANTE: 'PARTICIPANTE',
  NAO_PARTICIPANTE: 'NAO_PARTICIPANTE',
};

const montarOpcaoPartePncp = (cnpj, razaoSocial, unidade) => {
  const codigoUnidade = String(unidade?.codigoUnidade ?? '').trim();
  const nomeUnidade = String(unidade?.nomeUnidade ?? '').trim();
  const orgaoLabel = razaoSocial || formatarCnpj(cnpj);

  return {
    cnpj,
    razaoSocial: razaoSocial || '',
    codigoUnidade,
    nomeUnidade,
    label: nomeUnidade
      ? `${orgaoLabel} — ${codigoUnidade} — ${nomeUnidade}`
      : `${orgaoLabel} — ${codigoUnidade}`,
  };
};

export default {
  name: 'ArpIntegradaDetail',
  filters: {
    fileServer,
  },
  props: {
    ata: {
      type: Object,
      required: true,
    },
    desabilitado: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      arquivoDocumento: null,
      parteForm: {
        tipoParticipante: TIPO_PARTICIPANTE.NAO_PARTICIPANTE,
        codigoUnidadeCompradora: null,
      },
      parteBusca: '',
      opcoesPartePncp: [],
      parteSelecionada: null,
      carregandoUnidadesPncp: false,
      ultimoCnpjConsultadoPncp: null,
      partePncpInvalida: false,
      consultarUnidadesPncpDebounced: null,
    };
  },
  created() {
    this.consultarUnidadesPncpDebounced = debounce(this.consultarUnidadesPncp, 400);
  },
  computed: {
    ...mapGetters({
      ofertaCompra: 'obterOfertaCompra',
    }),
    ofertaCompraId() {
      return this.ofertaCompra?.id || this.$route.params.id || null;
    },
    pncpDocumentoUploadTooltip() {
      return PNCP_DOCUMENT_UPLOAD_TOOLTIP;
    },
    integrado() {
      return (
        this.ata.pncpIntegrado === true ||
        this.ata.integrationStatus === 'INTEGRADO' ||
        (this.ata.sequencialAta != null && String(this.ata.sequencialAta).trim() !== '')
      );
    },
    permiteAdesao() {
      return (
        this.ata.possibilidadeAdesao === true ||
        this.ata.possibilidadeAdesao === 'true' ||
        this.ata.possibilidadeAdesao === 1
      );
    },
    documentos() {
      return Array.isArray(this.ata.documentos) ? this.ata.documentos : [];
    },
    partesEnvolvidas() {
      return Array.isArray(this.ata.partesEnvolvidas) ? this.ata.partesEnvolvidas : [];
    },
    partesEnvolvidasVisiveis() {
      return this.partesEnvolvidas.filter(
        parte => Number(parte?.tipoParteEnvolvidaId) !== TIPO_PARTE_GERENCIADORA,
      );
    },
    documentosFormDesabilitado() {
      return !this.integrado || this.desabilitado;
    },
    partesFormDesabilitado() {
      return !this.integrado || this.desabilitado;
    },
    tipoParticipanteEfetivo() {
      if (!this.permiteAdesao) {
        return TIPO_PARTICIPANTE.PARTICIPANTE;
      }
      return this.parteForm.tipoParticipante;
    },
    opcoesPartePncpFiltradas() {
      if (this.parteSelecionada) {
        return this.opcoesPartePncp;
      }

      const termo = String(this.parteBusca || '')
        .trim()
        .toLowerCase();
      if (!termo) {
        return this.opcoesPartePncp;
      }

      return this.opcoesPartePncp.filter(opcao => {
        const label = String(opcao.label || '').toLowerCase();
        const cnpj = String(opcao.cnpj || '').toLowerCase();
        const razaoSocial = String(opcao.razaoSocial || '').toLowerCase();
        const codigo = String(opcao.codigoUnidade || '').toLowerCase();
        const nome = String(opcao.nomeUnidade || '').toLowerCase();
        return (
          label.includes(termo) ||
          cnpj.includes(termo.replace(/\D/g, '')) ||
          razaoSocial.includes(termo) ||
          codigo.includes(termo) ||
          nome.includes(termo)
        );
      });
    },
  },
  watch: {
    permiteAdesao(val) {
      if (!val) {
        this.parteForm.tipoParticipante = TIPO_PARTICIPANTE.PARTICIPANTE;
      }
    },
  },
  methods: {
    limparConsultaPartePncp() {
      this.opcoesPartePncp = [];
      this.parteSelecionada = null;
      this.parteBusca = '';
      this.parteForm.codigoUnidadeCompradora = null;
      this.ultimoCnpjConsultadoPncp = null;
      this.partePncpInvalida = false;
    },
    onParteBuscaInput() {
      this.partePncpInvalida = false;

      if (this.parteSelecionada && this.parteBusca !== this.parteSelecionada.label) {
        this.parteSelecionada = null;
        this.parteForm.codigoUnidadeCompradora = null;
      }

      const digits = limparDocumento(this.parteBusca);

      if (digits.length !== 14) {
        if (this.ultimoCnpjConsultadoPncp) {
          this.opcoesPartePncp = [];
          this.ultimoCnpjConsultadoPncp = null;
        }
        return;
      }

      if (!isValidCnpj(digits)) {
        this.opcoesPartePncp = [];
        this.ultimoCnpjConsultadoPncp = null;
        return;
      }

      if (!this.parteSelecionada && this.parteBusca !== formatarCnpj(digits)) {
        this.parteBusca = formatarCnpj(digits);
      }

      if (this.parteSelecionada || digits === this.ultimoCnpjConsultadoPncp) {
        return;
      }

      this.consultarUnidadesPncpDebounced(digits);
    },
    async consultarUnidadesPncp(cnpj) {
      if (!this.ofertaCompraId || !cnpj || this.parteSelecionada) {
        return;
      }

      this.carregandoUnidadesPncp = true;
      this.opcoesPartePncp = [];
      this.parteForm.codigoUnidadeCompradora = null;
      this.partePncpInvalida = false;
      this.ultimoCnpjConsultadoPncp = cnpj;

      try {
        const { data: payload } = await consultarAtaPrecoParteEnvolvidaPncpUnidades(
          this.ofertaCompraId,
          cnpj,
        );
        const body = payload?.data ?? payload;
        const razaoSocial = body?.orgao?.razaoSocial || '';
        const unidades = Array.isArray(body?.unidades) ? body.unidades : [];

        this.opcoesPartePncp = unidades
          .map(unidade => montarOpcaoPartePncp(cnpj, razaoSocial, unidade))
          .filter(opcao => opcao.codigoUnidade);

        this.$nextTick(() => {
          const autocomplete = this.$refs.partePncp;
          if (autocomplete && this.opcoesPartePncp.length) {
            autocomplete.isActive = true;
          }
        });
      } catch (error) {
        this.ultimoCnpjConsultadoPncp = null;
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Erro ao consultar unidades no PNCP';
        this.$alerta(msg, 'is-danger');
      } finally {
        this.carregandoUnidadesPncp = false;
      }
    },
    onSelecionarPartePncp(opcao) {
      if (!opcao?.cnpj || !opcao?.codigoUnidade) {
        this.parteSelecionada = null;
        this.parteForm.codigoUnidadeCompradora = null;
        return;
      }

      this.parteSelecionada = opcao;
      this.parteForm.codigoUnidadeCompradora = opcao.codigoUnidade;
      this.parteBusca = opcao.label;
      this.partePncpInvalida = false;
    },
    onCarregarDocumento() {
      const arquivo = this.arquivoDocumento;
      if (!arquivo) return;

      const file = Array.isArray(arquivo) ? arquivo[0] : arquivo;
      this.arquivoDocumento = null;
      this.$emit('carregar-documento', { ata: this.ata, arquivo: file });
    },
    adicionarParte() {
      if (!this.parteSelecionada?.cnpj || !this.parteSelecionada?.codigoUnidade) {
        this.partePncpInvalida = true;
        this.$alerta('Informe o CNPJ e selecione a unidade cadastrada no PNCP', 'is-warning');
        return;
      }

      const digits = limparDocumento(this.parteSelecionada.cnpj);
      if (digits.length !== 14 || !isValidCnpj(digits)) {
        this.partePncpInvalida = true;
        this.$alerta('Informe um CNPJ válido', 'is-warning');
        return;
      }

      this.$emit('adicionar-parte', {
        ata: this.ata,
        documento: digits,
        tipoParticipante: this.tipoParticipanteEfetivo,
        codigoUnidadeCompradora: this.parteSelecionada.codigoUnidade,
        nomeUnidadeCompradora: this.parteSelecionada.nomeUnidade || null,
      });

      this.limparConsultaPartePncp();
      if (this.permiteAdesao) {
        this.parteForm.tipoParticipante = TIPO_PARTICIPANTE.NAO_PARTICIPANTE;
      }
    },
    rotuloTipoParticipante(tipo) {
      if (tipo === 'GERENCIADORA') return 'Gerenciadora';
      return tipo === TIPO_PARTICIPANTE.NAO_PARTICIPANTE
        ? 'Não Participante (Carona)'
        : 'Participante';
    },
    podeExcluirParte(parte) {
      return Number(parte?.tipoParteEnvolvidaId) !== TIPO_PARTE_GERENCIADORA;
    },
    podeExcluirDocumento(documento) {
      return Number(documento?.tipoDocumentoId) !== TIPO_DOCUMENTO_ARP_PRINCIPAL;
    },
    documentoSincronizadoPncp(documento) {
      if (documento?.pncpIntegrado === true) return true;
      return (
        documento?.sequencialDocumento != null &&
        String(documento.sequencialDocumento).trim() !== ''
      );
    },
    parteSincronizadaPncp(parte) {
      return parte?.pncpIntegrado === true;
    },
    razaoSocialParte(parte) {
      const nome = String(parte?.nome || '').trim();
      const documento = limparDocumento(parte?.documento);
      const nomeLimpo = limparDocumento(nome);

      if (!nome || (nomeLimpo.length === 14 && nomeLimpo === documento)) {
        return '';
      }

      return nome;
    },
    nomeExibicaoParte(parte) {
      const razaoSocial = this.razaoSocialParte(parte);
      if (razaoSocial) {
        return razaoSocial;
      }

      const nome = String(parte?.nome || '').trim();
      const documentoFormatado = this.formatarDocumentoParte(parte?.documento);
      if (nome && nome !== documentoFormatado) {
        return nome;
      }

      return '—';
    },
    detalheUnidadeParte(parte) {
      return this.detalheUnidade(
        parte?.nomeUnidadeCompradora || parte?.nomeUnidade,
        parte?.codigoUnidadeCompradora,
      );
    },
    nomeExibicaoOpcaoPncp(opcao) {
      const razaoSocial = String(opcao?.razaoSocial || '').trim();
      if (razaoSocial) {
        return razaoSocial;
      }

      return this.formatarDocumentoParte(opcao?.cnpj) || '—';
    },
    detalheUnidadeOpcaoPncp(opcao) {
      return this.detalheUnidade(opcao?.nomeUnidade, opcao?.codigoUnidade);
    },
    detalheUnidade(unidade, codigo) {
      const nomeUnidade = String(unidade || '').trim();
      const codigoUnidade = String(codigo || '').trim();

      if (!nomeUnidade && !codigoUnidade) {
        return '';
      }

      if (!nomeUnidade) {
        return codigoUnidade;
      }

      if (!codigoUnidade) {
        return nomeUnidade;
      }

      return `${nomeUnidade} ${codigoUnidade}`;
    },
    formatarDocumentoParte(documento) {
      const digits = limparDocumento(documento);
      if (digits.length === 14) return formatarCnpj(digits);
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
      return documento || '—';
    },
  },
};
</script>

<style scoped>
.arp-integrada {
  color: #4a4a4a;
  font-size: 0.875rem;
}

.arp-integrada__colunas {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0;
}

.arp-integrada__coluna {
  display: flex;
  flex: 1 1 calc(50% - 0.5rem);
  min-width: 0;
  width: calc(50% - 0.5rem);
}

.arp-integrada__card {
  background-color: #fff;
  border: 1px solid #b5b5b5;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(10, 10, 10, 0.06);
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.arp-integrada__section-head {
  align-items: center;
  background-color: #f6f7fa;
  border-bottom: 1px solid #dbdbdb;
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0;
  padding: 0.75rem 1rem;
}

.arp-integrada__section-title {
  color: #363636;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.arp-integrada__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.arp-integrada__item {
  align-items: center;
  border-bottom: 1px solid #ededed;
  display: flex;
  flex-wrap: nowrap;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
}

.arp-integrada__item:last-child {
  border-bottom: none;
}

.arp-integrada__item-nome {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}

.arp-integrada__item-nome a {
  color: #3273dc;
}

.arp-integrada__item-actions {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  margin-left: auto;
}

.arp-integrada__btn-excluir {
  flex: 0 0 auto;
  height: 2.25rem;
  margin-bottom: 0;
  min-width: 2.25rem;
  padding-left: 0.65em;
  padding-right: 0.65em;
}

.arp-integrada__status-icon {
  align-items: center;
  display: inline-flex;
  height: 2.25rem;
  justify-content: center;
  line-height: 1;
  width: 2.25rem;
}

.arp-integrada__status-icon >>> .icon {
  height: 1.5rem;
  width: 1.5rem;
}

.arp-integrada__status-icon.is-pending >>> .icon {
  color: #f2a900;
}

.arp-integrada__empty {
  padding: 1rem;
}

.arp-integrada__partes-form {
  align-items: flex-end;
  border-bottom: 1px solid #ededed;
  margin: 0;
  padding: 0.85rem 1rem;
}

.arp-integrada__partes-form > .field {
  margin-bottom: 0;
  margin-right: 0;
}

.arp-integrada__partes-form > .field:not(:last-child) {
  margin-right: 0.75rem;
}

.arp-integrada__partes-orgao {
  flex: 1 1 0;
  min-width: 0;
}

.arp-integrada__partes-autocomplete {
  width: 100%;
}

.arp-integrada__partes-autocomplete >>> .dropdown-item {
  border-bottom: 1px solid #dbdbdb;
  padding: 0.75rem 1rem;
  white-space: normal;
}

.arp-integrada__partes-autocomplete >>> .dropdown-item:last-child {
  border-bottom: none;
}

.arp-integrada__partes-tipo {
  flex: 1 1 0;
  min-width: 0;
}

.arp-integrada__partes-adicionar {
  flex: 0 0 auto;
}

.arp-integrada__parte-linha-principal {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0;
}

.arp-integrada__parte-linha-secundaria {
  color: #7a7a7a;
  font-size: 0.85rem;
  margin: 0.25rem 0 0;
}

.arp-integrada__parte-documento {
  color: #7a7a7a;
  font-weight: 400;
}

.arp-integrada__parte-tag {
  background-color: #f3f4f6;
  border: none;
  color: #363636;
  flex: 0 0 auto;
  font-size: 0.875rem;
  margin-bottom: 0;
}

@media (max-width: 1023px) {
  .arp-integrada__coluna {
    flex: 1 1 100%;
    max-width: 100%;
    width: 100%;
  }

  .arp-integrada__partes-form {
    flex-direction: column;
    align-items: stretch;
  }

  .arp-integrada__partes-orgao,
  .arp-integrada__partes-tipo,
  .arp-integrada__partes-adicionar {
    flex: 1 1 auto;
    margin-right: 0 !important;
    width: 100%;
  }

  .arp-integrada__partes-adicionar .button {
    width: 100%;
  }

  .arp-integrada__item {
    align-items: flex-start;
  }

  .arp-integrada__item-actions {
    align-items: flex-start;
  }
}
</style>
